import { useState, useContext, createContext } from 'react'
import { renderToString } from 'react-dom/server'
import { PDFDocument, rgb, StandardFonts } from 'pdf-lib'
import QRCode from 'qrcode'

import AuthService from '../../utilities/services/auth.service'

import { setTicketWasPrinted } from '../../utilities/api'

import {
  formatDateTime,
  getTimezoneDate,
  timezones,
  sortTickets,
  formatTicketLocationValue,
} from '../../utilities/helpers'
import {
  addBrandedTicketsToDocument,
  createTicketPdfFonts,
  formatTicketHolderName,
} from '../../utilities/ticketPdfTemplate'
import {
  formatPrintedOfferLine,
  resolvePrintedOfferInfo,
} from '@blocktickets/shared-utils'

// Create the context
const PrintTicketsContext = createContext(undefined)

export const usePrintTickets = () => {
  const context = useContext(PrintTicketsContext)
  if (!context) {
    throw new Error('Context must be used within a usePrintTickets')
  }
  return context
}

export const PrintTicketsProvider = ({ children }) => {
  // loading state
  const [preparingPDF, setPreparingPDF] = useState(false)
  const [preparingThermal, setPreparingThermal] = useState(false)
  const [preparingVouchersPDF, setPreparingVouchersPDF] = useState(false)
  const [preparingVouchersThermal, setPreparingVouchersThermal] =
    useState(false)
  const [preparingAccessPassesPDF, setPreparingAccessPassesPDF] =
    useState(false)
  const [preparingAccessPassesThermal, setPreparingAccessPassesThermal] =
    useState(false)
  const organization = AuthService.getOrg()[0]

  // Unified print function using iframe approach
  const printContent = (content, type) => {
    let blob, url

    if (type === 'html') {
      blob = new Blob([content], { type: 'text/html' })
    } else if (type === 'pdf') {
      blob = new Blob([content], { type: 'application/pdf' })
    }

    url = URL.createObjectURL(blob)

    // Create hidden iframe
    const iframe = document.createElement('iframe')
    iframe.style.position = 'absolute'
    iframe.style.left = '-9999px'
    iframe.style.width = '1px'
    iframe.style.height = '1px'
    iframe.src = url

    document.body.appendChild(iframe)

    // Cleanup function
    const cleanup = () => {
      if (document.body.contains(iframe)) {
        document.body.removeChild(iframe)
      }
      URL.revokeObjectURL(url)
    }

    iframe.onload = () => {
      try {
        // Open print dialog immediately after load
        iframe.contentWindow?.print()
        window.addEventListener('focus', cleanup, { once: true })
      } catch (error) {
        console.error('Print failed:', error)
        cleanup()
      }
    }
  }

  // Updated thermal print handler
  const handlePrintThermal = async (events, ticketsToPrint) => {
    if (!ticketsToPrint || events.length === 0) {
      console.warn('No Tickets or events selected')
      return
    }

    setPreparingThermal(true)
    try {
      const eventsMap = createEventsMap(events)
      const processedTickets = await processTicketsForThermal(
        ticketsToPrint,
        eventsMap
      )
      const sortedTickets = sortTickets(processedTickets)

      if (sortedTickets.length === 0) {
        console.warn('No valid tickets to print')
        return
      }

      const htmlContent = generateThermalHTML(sortedTickets)
      printContent(htmlContent, 'html')

      // Mark tickets as printed (fire and forget)
      markTicketsAsPrinted(ticketsToPrint)
    } catch (err) {
      console.log('Error in handlePrintThermal: ', err)
    } finally {
      setPreparingThermal(false)
    }
  }

  // Updated PDF print handler
  const handlePrintPDF = async (
    events,
    ticketsToPrint,
    holderInfo,
    purchasedByOrganizationName
  ) => {
    if (!ticketsToPrint || events.length === 0) {
      console.warn('No Tickets or events selected')
      return
    }

    setPreparingPDF(true)

    try {
      const eventsMap = createEventsMap(events)
      const ticketsWithEventStart = processTicketsForPDF(
        ticketsToPrint,
        eventsMap
      )
      const sortedTickets = sortTickets(ticketsWithEventStart)
      const ticketHolder =
        typeof holderInfo === 'string'
          ? formatTicketHolderName({ email: holderInfo })
          : formatTicketHolderName(holderInfo || {})

      const pdfDoc = await generatePDFDocument(
        sortedTickets,
        eventsMap,
        ticketHolder,
        purchasedByOrganizationName
      )
      const pdfBytes = await pdfDoc.save()

      printContent(pdfBytes, 'pdf')

      // Mark tickets as printed (fire and forget)
      markTicketsAsPrinted(sortedTickets)
    } catch (err) {
      console.log('Error in handlePrintPDF: ', err)
    } finally {
      setPreparingPDF(false)
    }
  }

  // Print vouchers as PDF — same layout as tickets but with the flex pack
  // name in place of the event name and the voucher code in place of the QR.
  const handlePrintVouchersPDF = async (flexPack, vouchersToPrint) => {
    if (!flexPack || !vouchersToPrint || vouchersToPrint.length === 0) {
      console.warn('No flex pack or vouchers selected')
      return
    }

    setPreparingVouchersPDF(true)
    try {
      const pdfDoc = await generateVouchersPDFDocument(
        vouchersToPrint,
        flexPack
      )
      const pdfBytes = await pdfDoc.save()
      printContent(pdfBytes, 'pdf')
    } catch (err) {
      console.log('Error in handlePrintVouchersPDF: ', err)
    } finally {
      setPreparingVouchersPDF(false)
    }
  }

  // Print vouchers via thermal printer (HTML route).
  const handlePrintVouchersThermal = async (flexPack, vouchersToPrint) => {
    if (!flexPack || !vouchersToPrint || vouchersToPrint.length === 0) {
      console.warn('No flex pack or vouchers selected')
      return
    }

    setPreparingVouchersThermal(true)
    try {
      const htmlContent = generateVouchersThermalHTML(vouchersToPrint, flexPack)
      printContent(htmlContent, 'html')
    } catch (err) {
      console.log('Error in handlePrintVouchersThermal: ', err)
    } finally {
      setPreparingVouchersThermal(false)
    }
  }

  // Print access passes as PDF — branded two-up layout matching tickets.
  const handlePrintAccessPassesPDF = async (
    template,
    passesToPrint,
    holderInfo
  ) => {
    if (!passesToPrint || passesToPrint.length === 0) {
      console.warn('No access passes selected')
      return
    }

    setPreparingAccessPassesPDF(true)
    try {
      const ticketHolder =
        typeof holderInfo === 'string'
          ? formatTicketHolderName({ email: holderInfo })
          : formatTicketHolderName(holderInfo || {})
      const pdfDoc = await generateAccessPassesPDFDocument(
        passesToPrint,
        template,
        ticketHolder
      )
      const pdfBytes = await pdfDoc.save()
      printContent(pdfBytes, 'pdf')
    } catch (err) {
      console.log('Error in handlePrintAccessPassesPDF: ', err)
    } finally {
      setPreparingAccessPassesPDF(false)
    }
  }

  // Print access passes via thermal printer (HTML route).
  const handlePrintAccessPassesThermal = async (template, passesToPrint) => {
    if (!passesToPrint || passesToPrint.length === 0) {
      console.warn('No access passes selected')
      return
    }

    setPreparingAccessPassesThermal(true)
    try {
      const htmlContent = await generateAccessPassesThermalHTML(
        passesToPrint,
        template
      )
      printContent(htmlContent, 'html')
    } catch (err) {
      console.log('Error in handlePrintAccessPassesThermal: ', err)
    } finally {
      setPreparingAccessPassesThermal(false)
    }
  }

  // HELPERS DOWN BELOW !!!!
  const processTicketForThermal = async (ticket, event) => {
    // Generate QR code — denser raster + modest quiet zone for thermal / XT4 scan
    const qrDataUrl = await QRCode.toDataURL(ticket.checkInCode, {
      errorCorrectionLevel: 'M',
      margin: 1,
      width: 256,
      color: { dark: '#000000', light: '#FFFFFF' },
    })
    if (!qrDataUrl) {
      console.warn('Could not generate QR code for ticket:', ticket)
      return null
    }

    // Process event names (case-insensitive vs split for IceDogs-style layouts)
    let trimmedEventName = event?.name?.trim() || ''
    trimmedEventName = trimmedEventName.replace(/\bvs\./gi, 'vs')
    const vsMatch = trimmedEventName.match(/\s+vs\s+/i)
    let homeTeam = trimmedEventName
    let awayTeam = ''
    if (vsMatch) {
      const vsIndex = trimmedEventName.search(/\s+vs\s+/i)
      homeTeam = trimmedEventName.slice(0, vsIndex).trim()
      awayTeam = trimmedEventName.slice(vsIndex + vsMatch[0].length).trim()
    }

    // Process date/time
    const timezone = timezones(event?.venue?.timezone)
    const date = getTimezoneDate(event?.start, timezone)
    const startDate = formatDateTime(date, 'dateOnly')
    const startTime = formatDateTime(date, 'timeOnly')
    const offerInfo = resolvePrintedOfferInfo(ticket)
    const offerLine = formatPrintedOfferLine(ticket)
    const compactDateTime = offerInfo
      ? `${date?.format?.('MMM D') || ''} ${offerInfo.shortLabel}`.trim()
      : date?.format?.('MMM D h:mmA') || ''
    const stockDateTime = offerInfo
      ? date?.format?.('ddd MMM D YYYY')?.toUpperCase() || ''
      : date?.format?.('ddd MMM D YYYY h:mmA')?.toUpperCase() || ''
    const eventCode = date?.format?.('MMDDYY') || ''

    return {
      ...ticket,
      eventStart: event?.start,
      eventName: trimmedEventName,
      homeTeam,
      awayTeam,
      startDate,
      startTime: offerInfo?.window || startTime,
      compactDateTime,
      stockDateTime,
      eventCode,
      offerLabel: offerInfo?.label || '',
      offerWindow: offerInfo?.window || '',
      offerLine,
      checkInCode: qrDataUrl,
      event,
    }
  }

  const processTicketsForThermal = async (ticketsToPrint, eventsMap) => {
    const processedTickets = []

    for (const ticket of ticketsToPrint) {
      const event = eventsMap[ticket.eventId]
      if (!event) {
        console.warn('Could not find matching event for ticket:', ticket)
        continue
      }

      const processedTicket = await processTicketForThermal(ticket, event)
      if (processedTicket) {
        processedTickets.push(processedTicket)
      }
    }

    return processedTickets
  }

  const processTicketsForPDF = (ticketsToPrint, eventsMap) => {
    return ticketsToPrint
      .map((ticket) => {
        const event = eventsMap[ticket.eventId]
        if (!event) {
          console.warn('Could not find matching event for ticket:', ticket)
          return null
        }
        return {
          ...ticket,
          eventStart: event?.start,
        }
      })
      .filter(Boolean)
  }

  const generateThermalHTML = (processedTickets) => {
    const organizationUUID = organization?.uuid
    const iceDogsOrgUUID = '001ba6b5-0595-4133-8ec9-1fa29df4154c'
    const nmStateOrgUUID = 'dbeea528-852e-43d0-b607-c8201d7a5f1c'

    if (organizationUUID === iceDogsOrgUUID) {
      return renderIceDogsTemplate(processedTickets)
    }
    if (organizationUUID === nmStateOrgUUID) {
      return renderNmStateTemplate(processedTickets)
    }
    return renderBasicTemplate(processedTickets)
  }

  const generatePDFDocument = async (
    sortedTickets,
    eventsMap,
    ticketHolder,
    purchasedByOrganizationName
  ) => {
    const pdfDoc = await PDFDocument.create()
    const fonts = await createTicketPdfFonts(pdfDoc)
    const logoCache = {}
    const ticketEntries = []

    for (const ticket of sortedTickets) {
      const qrDataUrl = await QRCode.toDataURL(ticket.checkInCode)
      if (!qrDataUrl) {
        console.warn('Could not make qrcode...')
        continue
      }

      const event = eventsMap[ticket.eventId]
      const timezone = timezones(event?.venue?.timezone)
      const zonedDate = getTimezoneDate(
        event?.start,
        event?.timezone || timezone
      )
      const qrImage = await pdfDoc.embedPng(qrDataUrl)
      ticketEntries.push({
        ticket,
        event,
        organization,
        purchasedByOrganizationName,
        qrImage,
        ticketHolder,
        dateLabel: formatDateTime(zonedDate, 'dateOnly'),
        timeLabel: formatDateTime(zonedDate, 'timeOnly'),
      })
    }

    await addBrandedTicketsToDocument(pdfDoc, ticketEntries, {
      fonts,
      logoCache,
    })

    return pdfDoc
  }

  const createEventsMap = (events) => {
    const eventsMap = {}
    events.forEach((event) => {
      if (!eventsMap[event.uuid]) {
        eventsMap[event.uuid] = event
      }
    })
    return eventsMap
  }

  const addVoucherPage = (pdfDoc, voucher, flexPack, fonts) => {
    const page = pdfDoc.addPage([400, 200])
    const { width, height } = page.getSize()
    const { helveticaBold } = fonts

    const organizationName = organization?.name || ''
    const flexPackName = flexPack?.name || 'Flex Pack'
    const voucherCode = voucher?.code || ''

    let cursorY = height - 25

    if (organizationName) {
      page.drawText(organizationName, {
        x: 20,
        y: cursorY,
        size: 11,
        font: helveticaBold,
        color: rgb(0.4, 0.4, 0.4),
      })
      cursorY -= 22
    }

    page.drawText(flexPackName, {
      x: 20,
      y: cursorY,
      size: 14,
      font: helveticaBold,
      color: rgb(0, 0, 0),
    })

    page.drawText('Voucher Code:', {
      x: 20,
      y: height - 95,
      size: 12,
      font: helveticaBold,
    })

    page.drawText(voucherCode, {
      x: 20,
      y: height - 125,
      size: 22,
      font: helveticaBold,
      color: rgb(0, 0, 0),
    })

    if (voucher?.claimed) {
      page.drawText('Redeemed', {
        x: 20,
        y: height - 160,
        size: 10,
        font: helveticaBold,
        color: rgb(0.5, 0.5, 0.5),
      })
    }
  }

  const generateVouchersPDFDocument = async (vouchers, flexPack) => {
    const pdfDoc = await PDFDocument.create()
    const embeddedFonts = {
      helveticaBold: await pdfDoc.embedFont(StandardFonts.HelveticaBold),
    }

    for (const voucher of vouchers) {
      addVoucherPage(pdfDoc, voucher, flexPack, embeddedFonts)
    }

    return pdfDoc
  }

  const generateVouchersThermalHTML = (vouchers, flexPack) => {
    const organizationName = organization?.name || ''
    const flexPackName = flexPack?.name || 'Flex Pack'
    const bucsOrgUUID = 'a9022e37-a64d-4f4d-bd5d-31a935526d06'
    const seasonValidity =
      String(organization?.uuid || '').toLowerCase() === bucsOrgUUID
        ? 'Valid 2026-27 Regular season'
        : ''

    const VoucherComponent = ({ voucher }) => (
      <div
        style={{
          padding: '5px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          width: '528px',
          minHeight: '172.8px',
          pageBreakInside: 'avoid',
          textAlign: 'center',
        }}
      >
        {organizationName && (
          <p
            style={{
              margin: '0 0 4px 0',
              fontSize: '12px',
              textTransform: 'uppercase',
              letterSpacing: '1px',
            }}
          >
            {organizationName}
          </p>
        )}
        <h2
          style={{
            margin: '0px',
            fontSize: '20px',
            textTransform: 'uppercase',
          }}
        >
          {flexPackName}
        </h2>
        <p
          style={{
            margin: '12px 0 4px 0',
            fontSize: '12px',
            textTransform: 'uppercase',
            letterSpacing: '1px',
          }}
        >
          Voucher Code
        </p>
        <p
          style={{
            margin: '0px',
            fontSize: '28px',
            fontWeight: 'bold',
            letterSpacing: '2px',
          }}
        >
          {voucher.code}
        </p>
        {seasonValidity && (
          <p
            style={{
              margin: '8px 0 0 0',
              fontSize: '12px',
            }}
          >
            {seasonValidity}
          </p>
        )}
        {voucher.claimed && (
          <p style={{ margin: '8px 0 0 0', fontSize: '12px' }}>Redeemed</p>
        )}
      </div>
    )

    const FullDocument = () => (
      <html lang="en">
        <head>
          <meta charSet="UTF-8" />
          <title>Print Vouchers</title>
          <style
            dangerouslySetInnerHTML={{
              __html: `
              @media print {
                body { margin: 0; }
                .voucher { page-break-inside: avoid; }
              }
            `,
            }}
          />
        </head>
        <body
          style={{
            fontFamily: 'monospace',
            margin: '20px',
            fontSize: '16px',
            fontWeight: '500',
          }}
        >
          <div id="vouchers">
            {vouchers.map((voucher, index) => (
              <VoucherComponent key={index} voucher={voucher} />
            ))}
          </div>
        </body>
      </html>
    )

    return `<!DOCTYPE html>${renderToString(<FullDocument />)}`
  }

  const generateAccessPassesPDFDocument = async (
    passes,
    template,
    ticketHolder
  ) => {
    const pdfDoc = await PDFDocument.create()
    const fonts = await createTicketPdfFonts(pdfDoc)
    const logoCache = {}
    const ticketEntries = []
    const organizationBrandingPrimary =
      organization?.branding?.enabled !== false
        ? organization?.branding?.primaryColor
        : null
    const passPrimaryColor =
      organizationBrandingPrimary ||
      organization?.primaryColor ||
      organization?.brandColor ||
      template?.primaryColor ||
      null

    const passOrganization = {
      ...organization,
      branding: {
        ...(organization?.branding || {}),
        primaryColor: passPrimaryColor,
      },
    }

    for (const pass of passes) {
      let qrImage = null
      if (pass?.checkInCode) {
        try {
          const qrDataUrl = await QRCode.toDataURL(pass.checkInCode)
          if (qrDataUrl) {
            qrImage = await pdfDoc.embedPng(qrDataUrl)
          }
        } catch (err) {
          console.warn('Could not generate QR for access pass:', pass, err)
        }
      }

      ticketEntries.push({
        ticket: {
          generalAdmission: Boolean(pass?.generalAdmission),
          sectionNumber: pass?.sectionNumber,
          rowNumber: pass?.rowNumber,
          seatNumber: pass?.seatNumber,
        },
        event: {
          name: pass?.name || template?.name || 'Access Pass',
          summary: organization?.name || 'Access Pass',
          category: { name: 'Access Pass' },
          primaryColor: passPrimaryColor,
          venue: {
            name: organization?.name || '',
          },
        },
        organization: passOrganization,
        qrImage,
        ticketHolder,
        dateLabel: pass?.type
          ? String(pass.type).replace(/_/g, ' ')
          : 'All assigned events',
        timeLabel: '—',
      })
    }

    await addBrandedTicketsToDocument(pdfDoc, ticketEntries, {
      fonts,
      logoCache,
    })

    return pdfDoc
  }

  const generateAccessPassesThermalHTML = async (passes, template) => {
    const organizationName = organization?.name || ''
    const templateName = template?.name || 'Access Pass'

    const processedPasses = await Promise.all(
      passes.map(async (pass) => {
        let qrDataUrl = null
        if (pass?.checkInCode) {
          try {
            qrDataUrl = await QRCode.toDataURL(pass.checkInCode, {
              errorCorrectionLevel: 'M',
              margin: 1,
              width: 256,
              color: { dark: '#000000', light: '#FFFFFF' },
            })
          } catch (err) {
            console.warn('Could not generate QR for access pass:', pass, err)
          }
        }

        let location = 'Access Pass'
        if (pass?.generalAdmission && pass?.sectionNumber) {
          location = `General Admission: ${pass.sectionNumber}`
        } else if (pass?.sectionNumber) {
          location = `Sec ${pass.sectionNumber} • Row ${pass?.rowNumber} • Seat ${pass?.seatNumber}`
        }

        return {
          ...pass,
          passName: pass?.name || templateName,
          location,
          qrDataUrl,
        }
      })
    )

    const AccessPassComponent = ({ pass }) => (
      <div
        style={{
          padding: '5px',
          display: 'flex',
          width: '528px',
          minHeight: '172.8px',
          alignItems: 'center',
          justifyContent: 'space-between',
          pageBreakInside: 'avoid',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '6px',
            flexGrow: 1,
            paddingRight: '12px',
          }}
        >
          {organizationName && (
            <p
              style={{
                margin: '0',
                fontSize: '12px',
                textTransform: 'uppercase',
                letterSpacing: '1px',
              }}
            >
              {organizationName}
            </p>
          )}
          <h2
            style={{
              margin: '0px',
              fontSize: '18px',
              textTransform: 'uppercase',
            }}
          >
            {pass.passName}
          </h2>
          <p style={{ margin: '0px', fontSize: '14px' }}>{pass.location}</p>
          {pass.checkInCode && (
            <p style={{ margin: '0px', fontSize: '12px' }}>
              Code: {pass.checkInCode}
            </p>
          )}
        </div>
        {pass.qrDataUrl ? (
          <img
            src={pass.qrDataUrl}
            alt="QR Code"
            style={{ width: '100px', height: '100px' }}
          />
        ) : (
          <p style={{ margin: '0px', fontSize: '12px' }}>QR Missing</p>
        )}
      </div>
    )

    const FullDocument = () => (
      <html lang="en">
        <head>
          <meta charSet="UTF-8" />
          <title>Print Access Passes</title>
          <style
            dangerouslySetInnerHTML={{
              __html: `
              @media print {
                body { margin: 0; }
              }
            `,
            }}
          />
        </head>
        <body
          style={{
            fontFamily: 'monospace',
            margin: '20px',
            fontSize: '16px',
            fontWeight: '500',
          }}
        >
          <div id="access-passes">
            {processedPasses.map((pass, index) => (
              <AccessPassComponent key={index} pass={pass} />
            ))}
          </div>
        </body>
      </html>
    )

    return `<!DOCTYPE html>${renderToString(<FullDocument />)}`
  }

  const markTicketsAsPrinted = (tickets) => {
    setTicketWasPrinted({
      tickets: tickets,
    })?.catch?.((err) => {
      console.log('Error in setTicketWasPrinted: ', err)
    })
  }

  // Helper functions from your templates
  const truncate = (str, len = 6) => {
    if (str == null) return ''
    const value = String(str)
    return value.length > len ? value.slice(0, len) : value
  }

  const getSectionName = (section) => {
    switch (section) {
      case 'Suite Overflow':
        return 'SUITE'
      case 'Standing Room Only':
        return 'SRO'
      case 'Carlsberg Lounge':
        return 'CL'
      case 'Ice-Level Club':
        return 'ILC'
      case 'General Admission':
        return 'GA'
      default:
        return section
    }
  }

  const formatAdmissionPrice = (ticket) => {
    const raw = ticket?.cost ?? ticket?.price ?? ticket?.amount
    const amount = Number.parseFloat(raw)
    if (!Number.isFinite(amount)) return ''
    return `$${amount.toFixed(amount % 1 === 0 ? 0 : 2)}`
  }

  const getSeatValue = (ticket, field) => {
    if (ticket.generalAdmission) return ''
    const value = ticket?.[field]
    return value == null || value === '' ? '' : truncate(String(value))
  }

  const getPackageName = (ticket, eventName) => {
    const related = ticket?.package
    const pkg = Array.isArray(related) ? related[0] : related
    const name = String(pkg?.name || ticket?.packageName || '').trim()
    // Package purchases title the ticket with the package itself; don't repeat it.
    return name === String(eventName || '').trim() ? '' : name
  }

  // Niagara IceDogs 3-part perforated thermal layout
  const renderIceDogsTemplate = (tickets) => {
    const TicketComponent = ({ ticket }) => (
      <div
        style={{
          padding: '5px',
          display: 'flex',
          width: '528px',
          height: '172.8px',
          alignItems: 'center',
          pageBreakInside: 'avoid',
        }}
      >
        <div style={{ display: 'flex' }}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '2px',
              border: '1px solid transparent',
              flexBasis: '0',
              minWidth: '0',
              flexGrow: '1',
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
                marginBottom: '8px',
              }}
            >
              <span>
                <span style={{ fontSize: '12px' }}>Sect:</span>{' '}
                {getSectionName(ticket.sectionNumber)}
              </span>
              <span>
                <span style={{ fontSize: '12px' }}>Row:</span>{' '}
                {truncate(
                  String(
                    formatTicketLocationValue(
                      ticket.rowNumber,
                      ticket.generalAdmission ? 'GA' : ''
                    )
                  )
                )}
              </span>
              <span>
                <span style={{ fontSize: '12px' }}>Seat:</span>{' '}
                {truncate(
                  String(
                    formatTicketLocationValue(
                      ticket.seatNumber,
                      ticket.generalAdmission ? 'GA' : ''
                    )
                  )
                )}
              </span>
            </div>
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '2px',
              border: '1px solid transparent',
              flexBasis: '0',
              minWidth: '0',
              flexGrow: '1',
            }}
          ></div>
        </div>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '2px',
            border: '1px solid transparent',
            flexBasis: '0',
            minWidth: '0',
            flexGrow: '1',
            textAlign: 'center',
          }}
        >
          <h2
            style={{
              margin: '0px',
              fontSize: '24px',
              textTransform: 'uppercase',
            }}
          >
            {ticket.awayTeam}
          </h2>
          <p style={{ margin: '0px', whiteSpace: 'nowrap' }}>vs</p>
          <h2
            style={{
              margin: '0px',
              fontSize: '24px',
              textTransform: 'uppercase',
            }}
          >
            {ticket.homeTeam}
          </h2>
          <p style={{ margin: '0px', whiteSpace: 'nowrap' }}>
            {ticket.event.venue.name}
          </p>
          <p style={{ margin: '0px', whiteSpace: 'nowrap' }}>
            {ticket.startDate}
          </p>
          <p style={{ margin: '0px', whiteSpace: 'nowrap' }}>
            {ticket.startTime}
          </p>
        </div>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '2px',
            border: '1px solid transparent',
            flexBasis: '0',
            minWidth: '0',
            flexGrow: '1',
            alignItems: 'flex-start',
            justifyContent: 'center',
          }}
        >
          <div
            style={{
              marginLeft: '153px',
              marginBottom: '20px',
              marginTop: '20px',
              // Preprinted IceDogs stock has "DOG COUNTRY" at the top of
              // the stub; shift the QR + seat line down so they do not overlap.
              transform: 'translateY(20px)',
            }}
          >
            <img
              src={ticket.checkInCode}
              style={{
                width: '120px',
                height: '120px',
              }}
              alt="Check-in code"
            />
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                gap: '16px',
                marginTop: '-13px',
              }}
            >
              <span>{getSectionName(ticket.sectionNumber)}</span>
              <span>
                {truncate(
                  String(
                    formatTicketLocationValue(
                      ticket.rowNumber,
                      ticket.generalAdmission ? 'GA' : ''
                    )
                  )
                )}
              </span>
              <span>
                {truncate(
                  String(
                    formatTicketLocationValue(
                      ticket.seatNumber,
                      ticket.generalAdmission ? 'GA' : ''
                    )
                  )
                )}
              </span>
            </div>
          </div>
        </div>
      </div>
    )

    const FullDocument = () => (
      <html lang="en">
        <head>
          <meta charSet="UTF-8" />
          <title>Print Tickets</title>
          <style
            dangerouslySetInnerHTML={{
              __html: `
              @media print { 
                body { margin: 0; } 
                .ticket { page-break-inside: avoid; }
              }
            `,
            }}
          />
        </head>
        <body
          style={{
            fontFamily: 'monospace',
            margin: '20px',
            fontSize: '16px',
            fontWeight: '500',
          }}
        >
          <div id="tickets">
            {tickets.map((ticket, index) => (
              <TicketComponent key={index} ticket={ticket} />
            ))}
          </div>
        </body>
      </html>
    )

    return `<!DOCTYPE html>${renderToString(<FullDocument />)}`
  }

  // NM State Athletics preprinted fanfold 3-part stock (Boca).
  // Print canvas is landscape: left stub | event body | admission stub.
  // When held vertically that maps to top stub | middle | bottom stub.
  // Stock already has maroon labels — print values only into the white boxes.
  // Do not print into the bottom-stub logo/footer zone (roughly x > 430).
  const renderNmStateTemplate = (tickets) => {
    const TicketComponent = ({ ticket }) => {
      // Prefer real section/row/seat values (incl. GA assignments like GA1 / GA0 / 5)
      const section =
        getSectionName(ticket.sectionNumber) ||
        (ticket.generalAdmission ? 'GA' : '')
      const row = truncate(
        String(
          formatTicketLocationValue(
            ticket.rowNumber,
            ticket.generalAdmission ? 'GA' : ''
          )
        )
      )
      const seat = truncate(
        String(
          formatTicketLocationValue(
            ticket.seatNumber,
            ticket.generalAdmission ? 'GA' : ''
          )
        )
      )
      const admission = formatAdmissionPrice(ticket)
      const dateTime =
        ticket.stockDateTime ||
        [ticket.startDate, ticket.startTime].filter(Boolean).join(' ')
      const compactDateTime = ticket.compactDateTime || ''
      const eventName = (ticket.eventName || ticket.event?.name || '').trim()
      const venueName = (ticket.event?.venue?.name || '').trim()
      const packageName = ticket.offerLine || getPackageName(ticket, eventName)
      const ticketId = ticket.id == null ? '' : String(ticket.id)
      const seatValueStyle = {
        textAlign: 'center',
        fontWeight: 900,
        letterSpacing: '-0.8px',
        lineHeight: 0.95,
      }

      return (
        <div
          style={{
            position: 'relative',
            width: '528px',
            height: '172.8px',
            pageBreakInside: 'avoid',
            overflow: 'hidden',
            fontFamily: "'Courier New', Courier, 'Nimbus Mono PS', monospace",
            fontWeight: 900,
            color: '#000',
          }}
        >
          {/* Top stub: large SECTION / ROW / SEAT values (near perforation) */}
          <div
            style={{
              position: 'absolute',
              left: '40px',
              top: '26px',
              width: '48px',
              height: '120px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              alignItems: 'center',
              fontSize: '24px',
              lineHeight: 0.95,
              textTransform: 'uppercase',
              paddingTop: '4px',
              paddingBottom: '4px',
              boxSizing: 'border-box',
            }}
          >
            <span style={seatValueStyle}>{section}</span>
            <span style={seatValueStyle}>{row}</span>
            <span style={seatValueStyle}>{seat}</span>
          </div>

          {/* Middle body: large event details, matching the legacy NMSU stock */}
          <div
            style={{
              position: 'absolute',
              left: '105px',
              top: '2px',
              width: '240px',
              height: '122px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              overflow: 'hidden',
              gap: '3px',
              textTransform: 'uppercase',
            }}
          >
            <div
              style={{
                fontSize: eventName.length > 48 ? '19px' : '25px',
                fontWeight: 900,
                lineHeight: 1,
                maxHeight: '50px',
                overflow: 'hidden',
                wordBreak: 'break-word',
                letterSpacing: '-1px',
              }}
            >
              {eventName}
            </div>
            {dateTime && (
              <div
                style={{
                  fontSize: '17px',
                  fontWeight: 900,
                  lineHeight: 1,
                  letterSpacing: '-0.8px',
                  whiteSpace: 'nowrap',
                }}
              >
                {dateTime}
              </div>
            )}
            {venueName && (
              <div
                style={{
                  fontSize: '16px',
                  fontWeight: 900,
                  lineHeight: 1,
                  letterSpacing: '-0.8px',
                  whiteSpace: 'nowrap',
                }}
              >
                {venueName}
              </div>
            )}
            {packageName && (
              <div
                style={{
                  fontSize: packageName.length > 30 ? '11px' : '13px',
                  fontWeight: 900,
                  lineHeight: 1,
                  letterSpacing: '-0.7px',
                  whiteSpace: 'nowrap',
                  maxWidth: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {packageName}
              </div>
            )}
            {ticketId && (
              <div
                style={{
                  fontSize: '11px',
                  fontWeight: 900,
                  lineHeight: 1,
                  letterSpacing: '-0.5px',
                  whiteSpace: 'nowrap',
                }}
              >
                TKT #{ticketId}
              </div>
            )}
          </div>

          {/* Middle maroon band — DATE + large SECTION / ROW / SEAT + ADMISSION */}
          <div
            style={{
              position: 'absolute',
              left: '96px',
              top: '136px',
              width: '270px',
              height: '34px',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              lineHeight: 0.95,
              textTransform: 'uppercase',
              padding: '0 2px',
              boxSizing: 'border-box',
            }}
          >
            <span
              style={{
                width: '72px',
                textAlign: 'center',
                fontSize: '13px',
                fontWeight: 900,
                letterSpacing: '-0.7px',
              }}
            >
              {compactDateTime}
            </span>
            <span
              style={{ ...seatValueStyle, width: '44px', fontSize: '21px' }}
            >
              {section}
            </span>
            <span
              style={{ ...seatValueStyle, width: '38px', fontSize: '21px' }}
            >
              {row}
            </span>
            <span
              style={{ ...seatValueStyle, width: '38px', fontSize: '21px' }}
            >
              {seat}
            </span>
            <span
              style={{
                width: '48px',
                textAlign: 'center',
                fontSize: '15px',
                fontWeight: 900,
                letterSpacing: '-0.7px',
              }}
            >
              {admission}
            </span>
          </div>

          {/* Bottom stub: QR ~0.75" for Janam XT4 (Honeywell N5703) */}
          <img
            src={ticket.checkInCode}
            alt="Check-in code"
            style={{
              position: 'absolute',
              left: '378px',
              top: '8px',
              width: '72px',
              height: '72px',
            }}
          />

          {/* Bottom stub SEC. / ROW / SEAT — large values above QR, across stub */}
          <div
            style={{
              position: 'absolute',
              left: '352px',
              top: '10px',
              width: '24px',
              height: '150px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              alignItems: 'center',
              fontSize: '22px',
              lineHeight: 0.95,
              textTransform: 'uppercase',
              paddingTop: '8px',
              paddingBottom: '8px',
              boxSizing: 'border-box',
            }}
          >
            <span style={seatValueStyle}>{section}</span>
            <span style={seatValueStyle}>{row}</span>
            <span style={seatValueStyle}>{seat}</span>
          </div>
        </div>
      )
    }

    const FullDocument = () => (
      <html lang="en">
        <head>
          <meta charSet="UTF-8" />
          <title>Print Tickets</title>
          <style
            dangerouslySetInnerHTML={{
              __html: `
              @media print {
                body { margin: 0; }
                .ticket { page-break-inside: avoid; }
              }
            `,
            }}
          />
        </head>
        <body
          style={{
            fontFamily: "'Courier New', Courier, 'Nimbus Mono PS', monospace",
            margin: '0',
            fontSize: '16px',
            fontWeight: 900,
          }}
        >
          <div id="tickets">
            {tickets.map((ticket, index) => (
              <TicketComponent key={index} ticket={ticket} />
            ))}
          </div>
        </body>
      </html>
    )

    return `<!DOCTYPE html>${renderToString(<FullDocument />)}`
  }

  const renderBasicTemplate = (tickets) => {
    const TicketComponent = ({ ticket }) => (
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          pageBreakInside: 'avoid',
        }}
      >
        <pre
          style={{
            marginLeft: '120px',
            fontSize: '11px',
            width: '260px',
            textAlign: 'center',
            margin: '0',
          }}
        >
          <span
            style={{
              fontWeight: 'bold',
              fontSize: '20px',
              textTransform: 'uppercase',
            }}
          >
            {ticket.homeTeam}
          </span>
          {'\nvs\n'}
          {ticket.awayTeam}
          {'\nSection: '}
          <span style={{ fontWeight: 'bold' }}>
            {truncate(ticket.sectionNumber, 12)}
          </span>
          {'   Row: '}
          <span style={{ fontWeight: 'bold' }}>
            {formatTicketLocationValue(
              ticket.rowNumber,
              ticket.generalAdmission ? 'GA' : ''
            )}
          </span>
          {'   Seat: '}
          <span style={{ fontWeight: 'bold' }}>
            {formatTicketLocationValue(
              ticket.seatNumber,
              ticket.generalAdmission ? 'GA' : ''
            )}
          </span>
          {'\n'}
          {[ticket.startDate, ticket.startTime].filter(Boolean).join('  ')}
          {ticket.offerLine ? `\n${ticket.offerLine}` : ''}
        </pre>

        <img
          src={ticket.checkInCode}
          style={{
            width: '90px',
            height: '90px',
            marginLeft: '-160px',
            marginTop: '90px',
            padding: '0px',
          }}
          alt="Check-in code"
        />

        <div
          style={{
            marginLeft: '155px',
            marginTop: '100px',
            fontSize: '12px',
            textAlign: 'center',
            transform: 'translateX(-50%) translateY(-50%) rotate(-90deg)',
          }}
        >
          <pre style={{ margin: '0' }}>
            Section {truncate(ticket.sectionNumber, 12)}
            {'\nRow '}
            {formatTicketLocationValue(
              ticket.rowNumber,
              ticket.generalAdmission ? 'GA' : ''
            )}
            {' Seat '}
            {formatTicketLocationValue(
              ticket.seatNumber,
              ticket.generalAdmission ? 'GA' : ''
            )}
          </pre>
        </div>
      </div>
    )

    const FullDocument = () => (
      <html lang="en">
        <head>
          <meta charSet="UTF-8" />
          <title>Print Tickets</title>
          <style
            dangerouslySetInnerHTML={{
              __html: `@media print { body { margin: 0; } }`,
            }}
          />
        </head>
        <body>
          <div id="tickets">
            {tickets.map((ticket, index) => (
              <TicketComponent key={index} ticket={ticket} />
            ))}
          </div>
        </body>
      </html>
    )

    return `<!DOCTYPE html>${renderToString(<FullDocument />)}`
  }

  return (
    <PrintTicketsContext.Provider
      value={{
        handlePrintThermal,
        handlePrintPDF,
        preparingPDF,
        preparingThermal,
        handlePrintVouchersPDF,
        handlePrintVouchersThermal,
        preparingVouchersPDF,
        preparingVouchersThermal,
        handlePrintAccessPassesPDF,
        handlePrintAccessPassesThermal,
        preparingAccessPassesPDF,
        preparingAccessPassesThermal,
      }}
    >
      {children}
    </PrintTicketsContext.Provider>
  )
}
